import { useEffect, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { isAnonymousCustomer } from '@/utils/conversationLabel';
import type { CustomerProfileDto } from '@/types';

/**
 * The CRM profile for one customer — the single fetch path for every surface that needs it.
 *
 * Three components consume it: the thread header's company chip (via {@link useCustomerCompany}),
 * the Conversation panel's collapsible contact section, and the Contact page. They are siblings with
 * no shared parent state, so before this hook existed the fetch was duplicated per surface.
 *
 * <b>Deliberately no cache.</b> A module-level cache leaks between tests and can serve a stale
 * company after a CRM edit; a store would put a fail-soft, CRM-shaped read into global state for no
 * gain. If caching is ever wanted it belongs HERE, inside the one hook, not at three call sites.
 * In practice the panel's contact section is collapsed by default, so `/conversations` makes FEWER
 * profile requests than it did before the split, not more.
 *
 * Two invariants, both inherited from the original panel and both load-bearing:
 *  - <b>Anonymous visitors are never looked up.</b> There is no identity to resolve, and a stub CRM
 *    provider would return fabricated data for one. Zero network calls in that case.
 *  - <b>Fail-soft.</b> A rejected lookup yields `notFound`, never a thrown error. A CRM outage must
 *    not be able to break the conversation an agent is working in.
 *
 * A falsy/blank `customerId` also makes zero requests, which is what lets a caller pass `null` to
 * defer the fetch until a collapsed section is actually opened.
 */
export interface CustomerProfileState {
  profile: CustomerProfileDto | null;
  loading: boolean;
  /**
   * The lookup FAILED (404 or transport error) — we know nothing about this customer.
   * Distinct from `profile.crmLinked === false`, which means the CRM has no contact but the server
   * still built a profile from our own `customer_identities` row: in that case the real email and
   * phone ARE present and must be shown.
   */
  notFound: boolean;
  /** No lookup was attempted at all, because the customer has no identity to look up. */
  anonymous: boolean;
}

const IDLE: CustomerProfileState = {
  profile: null,
  loading: false,
  notFound: false,
  anonymous: false,
};

export function useCustomerProfile(
  customerId: string | undefined | null,
): CustomerProfileState {
  const [state, setState] = useState<CustomerProfileState>(IDLE);

  useEffect(() => {
    const id = (customerId ?? '').trim();

    // No id ⇒ the caller is deferring (e.g. a collapsed section). Not anonymous, not loading.
    if (!id) {
      setState(IDLE);
      return;
    }

    if (isAnonymousCustomer(id)) {
      setState({ ...IDLE, anonymous: true });
      return;
    }

    let cancelled = false;
    setState({ profile: null, loading: true, notFound: false, anonymous: false });

    (async () => {
      try {
        const res = await axiosClient.get<CustomerProfileDto>(API.CRM.CUSTOMER(id));
        if (!cancelled) {
          setState({ profile: res.data, loading: false, notFound: false, anonymous: false });
        }
      } catch {
        if (!cancelled) {
          setState({ profile: null, loading: false, notFound: true, anonymous: false });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  return state;
}
